import React, { useMemo, useState } from "react";
import {
  MdCheckCircle,
  MdCancel,
  MdCreditCard,
  MdAutorenew,
  MdShoppingCart,
  MdInfo,
  MdReceipt,
  MdSchedule,
} from "react-icons/md";
import { SiStripe, SiAppstore } from "react-icons/si";
import Card from "components/card";
import {
  StripePurchase,
  RevenueCatSubscription,
  RevenueCatTransaction,
  UpcomingPlan,
} from "../../types/financial";
import {
  formatDiscountSubline,
  formatProductName,
} from "../../utils/formatters";
import { parseApiDate } from "../../utils/dateUtils";
import DetailsButton from "../common/DetailsButton";
import ErrorMessage from "../common/ErrorMessage";
import EmptyState from "../common/EmptyState";
import Button from "../common/Button";
import { badge } from "../../utils/badgeColors";
import { EMPTY_VALUE } from "../../utils/formatters";
import TransactionDetailsModal from "./TransactionDetailsModal";

interface TransactionHistoryCardProps {
  stripePurchases: StripePurchase[];
  revenueCatSubscriptions: RevenueCatSubscription[];
  revenueCatTransactions: RevenueCatTransaction[];
  // False only when RevenueCat could not be reached; stored rows are shown instead, if any.
  revenueCatHistoryAvailable?: boolean;
  // True when RevenueCat cut a subscription's list at the request limit.
  revenueCatHistoryHasMore?: boolean;
  // The plan a pending change moves the user onto; nothing has been paid for it yet.
  upcomingPlan?: UpcomingPlan | null;
  onRetryRevenueCatHistory?: () => void;
}

// Unified transaction type
type Transaction = {
  id: string;
  date: string;
  type: "purchase" | "subscription";
  provider: "stripe" | "revenuecat";
  productName: string;
  // Null when the provider quotes no price, as for a plan that has not started.
  amount: number | null;
  currency: string;
  discountSubline?: string | null;
  status: string;
  paymentMethod?: string;
  receiptUrl?: string;
  willRenew?: boolean;
  expiresAt?: string;
  store?: string;
  original:
    | StripePurchase
    | RevenueCatSubscription
    | RevenueCatTransaction
    | UpcomingPlan;
};

// A store transaction has no status of its own; it is read off its paid-through date.
const transactionStatus = (expiresAt?: string) => {
  if (!expiresAt) return "paid";
  return new Date(expiresAt).getTime() > Date.now() ? "active" : "expired";
};

const upcomingPlanRow = (plan: UpcomingPlan): Transaction => ({
  id: "upcoming-plan",
  date: plan.starts_at ?? plan.current_period_starts_at ?? "",
  type: "subscription",
  provider: plan.provider === "stripe" ? "stripe" : "revenuecat",
  productName: formatProductName(
    plan.store_product_id ?? plan.product_name ?? EMPTY_VALUE
  ),
  amount: plan.amount,
  currency: plan.currency ?? "",
  status: "upcoming",
  willRenew: plan.will_renew ?? undefined,
  expiresAt: plan.ends_at ?? plan.current_period_ends_at ?? undefined,
  store: plan.store ?? undefined,
  original: plan,
});

const formatAmount = ({ amount, currency }: Transaction) =>
  amount === null ? EMPTY_VALUE : `$${amount.toFixed(2)} ${currency}`;

const TransactionHistoryCard: React.FC<TransactionHistoryCardProps> = ({
  stripePurchases,
  revenueCatSubscriptions,
  revenueCatTransactions,
  revenueCatHistoryAvailable = true,
  revenueCatHistoryHasMore = false,
  upcomingPlan = null,
  onRetryRevenueCatHistory,
}) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);

  // Combine and sort all transactions by date
  const allTransactions = useMemo(() => {
    const transactions: Transaction[] = [];

    // Add Stripe purchases
    stripePurchases.forEach((purchase) => {
      transactions.push({
        id: `stripe-${purchase.id}`,
        date: purchase.created_at,
        type: "purchase",
        provider: "stripe",
        productName: purchase.product_name,
        amount: purchase.amount,
        currency: purchase.currency,
        discountSubline: formatDiscountSubline(purchase.discount),
        status: purchase.status,
        paymentMethod: purchase?.payment_method?.brand
          ? `${purchase.payment_method.brand} •••• ${purchase.payment_method.last4}`
          : undefined,
        receiptUrl: purchase.receipt_url,
        original: purchase,
      });
    });

    // Add RevenueCat history: one row per store transaction when RevenueCat answered
    // with rows, otherwise one per stored subscription chain, so an outage or an empty
    // sandbox never shows less than the table holds.
    if (revenueCatTransactions.length > 0) {
      revenueCatTransactions.forEach((tx, index) => {
        const expiresAt = tx.effective_expires_at ?? tx.expires_at ?? undefined;
        transactions.push({
          id: `revenuecat-tx-${tx.id ?? index}`,
          date: tx.purchased_at ?? "",
          type: "subscription",
          provider: "revenuecat",
          productName: formatProductName(tx.product_id ?? EMPTY_VALUE),
          amount: tx.price,
          currency: tx.currency,
          status: transactionStatus(expiresAt),
          expiresAt,
          store: tx.store ?? undefined,
          original: tx,
        });
      });
    } else {
      revenueCatSubscriptions.forEach((sub) => {
        transactions.push({
          id: `revenuecat-${sub.id}`,
          date: sub.purchased_at,
          type: "subscription",
          provider: "revenuecat",
          productName: formatProductName(sub.product_id),
          amount: parseFloat(sub.price), // Amount in dollars
          currency: sub.currency,
          status: sub.status,
          willRenew: sub.will_renew,
          expiresAt: sub.expires_at,
          store: sub.store,
          original: sub,
        });
      });
    }

    // Sort by date (newest first); a row without a date sorts last.
    const time = (date: string) => {
      const value = new Date(date).getTime();
      return Number.isNaN(value) ? 0 : value;
    };
    transactions.sort((a, b) => time(b.date) - time(a.date));

    // The upcoming plan is pinned first: it is what happens next, whatever its date.
    return upcomingPlan
      ? [upcomingPlanRow(upcomingPlan), ...transactions]
      : transactions;
  }, [
    stripePurchases,
    revenueCatSubscriptions,
    revenueCatTransactions,
    upcomingPlan,
  ]);

  const formatDate = (dateString: string) => {
    if (!dateString) return EMPTY_VALUE;
    const date = parseApiDate(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCardBrand = (brand: string) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
  };

  const handleViewDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedTransaction(null);
  };

  const getProviderBadge = (provider: "stripe" | "revenuecat") => {
    if (provider === "stripe") {
      return (
        <span className="inline-flex items-center rounded bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800 dark:bg-purple-800 dark:text-purple-100">
          <SiStripe className="mr-1 h-3 w-3" />
          Stripe
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-800 dark:text-blue-100">
          <SiAppstore className="mr-1 h-3 w-3" />
          RevenueCat
        </span>
      );
    }
  };

  const getTypeBadge = (type: "purchase" | "subscription") => {
    if (type === "purchase") {
      return (
        <span className="inline-flex items-center rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
          <MdShoppingCart className="mr-1 h-3 w-3" />
          Purchase
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
          <MdAutorenew className="mr-1 h-3 w-3" />
          Subscription
        </span>
      );
    }
  };

  const getStatusBadge = (status: string, isRefunded?: boolean) => {
    const statusLower = status.toLowerCase();

    if (isRefunded) {
      return (
        <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-800 dark:bg-orange-800 dark:text-orange-100">
          <MdCancel className="mr-1 h-3 w-3" />
          Refunded
        </span>
      );
    }

    if (
      statusLower === "succeeded" ||
      statusLower === "active" ||
      statusLower === "paid"
    ) {
      const label =
        statusLower === "succeeded"
          ? "Succeeded"
          : statusLower === "paid"
          ? "Paid"
          : "Active";
      return (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800 dark:bg-green-800 dark:text-green-100">
          <MdCheckCircle className="mr-1 h-3 w-3" />
          {label}
        </span>
      );
    } else if (statusLower === "expired") {
      return (
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${badge(
            "gray"
          )}`}
        >
          Expired
        </span>
      );
    } else if (
      statusLower === "failed" ||
      statusLower === "canceled" ||
      statusLower === "cancelled"
    ) {
      return (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-800 dark:bg-red-800 dark:text-red-100">
          <MdCancel className="mr-1 h-3 w-3" />
          {statusLower === "failed" ? "Failed" : "Canceled"}
        </span>
      );
    } else if (statusLower === "pending") {
      return (
        <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-1 text-xs font-semibold text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100">
          Pending
        </span>
      );
    } else if (statusLower === "upcoming") {
      return (
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${badge(
            "amber"
          )}`}
        >
          <MdSchedule className="mr-1 h-3 w-3" />
          Upcoming
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800 dark:bg-gray-800 dark:text-gray-100">
          {status}
        </span>
      );
    }
  };

  const hasRows = allTransactions.length > 0;
  const hasInAppRows = allTransactions.some(
    (transaction) => transaction.provider === "revenuecat"
  );

  // Replaces the empty state: an unreachable RevenueCat says nothing about what was bought.
  const unavailableMessage =
    revenueCatTransactions.length > 0
      ? "Couldn't reach RevenueCat for every subscription, so in-app purchases may be incomplete. This doesn't affect the user's access."
      : revenueCatSubscriptions.length > 0
      ? "Couldn't reach RevenueCat right now, so in-app purchases are shown from the subscription records saved in BiaFit. This doesn't affect the user's access."
      : "Couldn't reach RevenueCat right now, so in-app purchases can't be shown. This doesn't affect the user's access. Try again in a moment.";

  return (
    <Card extra="w-full p-3 md:p-6">
      <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
        Transaction History
      </h2>

      {!revenueCatHistoryAvailable && (
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <ErrorMessage message={unavailableMessage} className="flex-1" />
          {onRetryRevenueCatHistory && (
            <Button variant="secondary" onClick={onRetryRevenueCatHistory}>
              Retry
            </Button>
          )}
        </div>
      )}

      {!hasRows && revenueCatHistoryAvailable && (
        <EmptyState
          icon={MdReceipt}
          title="No transactions"
          description="This user has no Stripe payments or in-app purchases."
          padding="sm"
        />
      )}

      {/* Desktop Table */}
      {hasRows && (
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                  Date
                </th>
                <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                  Product
                </th>
                <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                  Type
                </th>
                <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                  Provider
                </th>
                <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                  Amount
                </th>
                <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                  Status
                </th>
                <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                  Details
                </th>
              </tr>
            </thead>
            <tbody>
              {allTransactions.map((transaction) => {
                const isStripePurchase =
                  transaction.provider === "stripe" &&
                  "is_refunded" in transaction.original;
                const isRefunded =
                  isStripePurchase &&
                  (transaction.original as StripePurchase).is_refunded;

                return (
                  <tr
                    key={transaction.id}
                    className="border-b border-gray-200 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                  >
                    <td className="py-4 text-sm text-gray-600 dark:text-gray-300">
                      {formatDate(transaction.date)}
                    </td>
                    <td className="py-4 text-sm text-navy-700 dark:text-white">
                      <div
                        className="max-w-xs truncate font-medium"
                        title={transaction.productName}
                      >
                        {transaction.productName}
                      </div>
                    </td>
                    <td className="py-4 text-sm">
                      {getTypeBadge(transaction.type)}
                    </td>
                    <td className="py-4 text-sm">
                      {getProviderBadge(transaction.provider)}
                    </td>
                    <td className="py-4 text-sm">
                      <div className="font-medium text-navy-700 dark:text-white">
                        {formatAmount(transaction)}
                      </div>
                      {transaction.discountSubline && (
                        <div className="mt-0.5 text-xs text-green-700 dark:text-green-400">
                          {transaction.discountSubline}
                        </div>
                      )}
                    </td>
                    <td className="py-4 text-sm">
                      {getStatusBadge(transaction.status, isRefunded)}
                    </td>
                    <td className="py-4 text-sm">
                      {/* View Details Button - Always shown */}
                      <DetailsButton
                        onClick={() => handleViewDetails(transaction)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile Cards */}
      {hasRows && (
        <div className="space-y-4 md:hidden">
          {allTransactions.map((transaction) => {
            const isStripePurchase =
              transaction.provider === "stripe" &&
              "is_refunded" in transaction.original;
            const isRefunded =
              isStripePurchase &&
              (transaction.original as StripePurchase).is_refunded;

            return (
              <div
                key={transaction.id}
                className="space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-1 font-medium text-navy-700 dark:text-white">
                      {transaction.productName}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(transaction.date)}
                    </div>
                  </div>
                  {getStatusBadge(transaction.status, isRefunded)}
                </div>

                <div className="flex items-center gap-2">
                  {getTypeBadge(transaction.type)}
                  {getProviderBadge(transaction.provider)}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                      Amount
                    </div>
                    <div className="font-medium text-navy-700 dark:text-white">
                      {formatAmount(transaction)}
                    </div>
                    {transaction.discountSubline && (
                      <div className="mt-0.5 text-xs text-green-700 dark:text-green-400">
                        {transaction.discountSubline}
                      </div>
                    )}
                  </div>

                  {transaction.paymentMethod && (
                    <div>
                      <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                        Payment Method
                      </div>
                      <div className="inline-flex items-center rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                        <MdCreditCard className="mr-1 h-3 w-3" />
                        {formatCardBrand(
                          transaction.paymentMethod.split(" ")[0]
                        )}{" "}
                        {transaction.paymentMethod.split(" ")[1]}
                      </div>
                    </div>
                  )}

                  {transaction.type === "subscription" &&
                    transaction.expiresAt && (
                      <div className="col-span-2">
                        <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                          {transaction.status === "upcoming"
                            ? "Ends"
                            : "Expires"}
                        </div>
                        <div className="text-navy-700 dark:text-white">
                          {formatDate(transaction.expiresAt)}
                        </div>
                      </div>
                    )}

                  {transaction.type === "subscription" &&
                    transaction.willRenew !== undefined && (
                      <div className="col-span-2">
                        <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                          Auto-Renew
                        </div>
                        {transaction.willRenew ? (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-800 dark:text-green-100">
                            <MdAutorenew className="mr-1 h-3 w-3" />
                            Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800 dark:bg-gray-800 dark:text-gray-100">
                            No
                          </span>
                        )}
                      </div>
                    )}
                </div>

                <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
                  {/* View Details Button - Always shown */}
                  <button
                    onClick={() => handleViewDetails(transaction)}
                    className="inline-flex w-full items-center justify-center rounded-md bg-biafit-primary px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-biafit-secondary"
                  >
                    <MdInfo className="mr-1.5 h-4 w-4" />
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasRows && revenueCatHistoryAvailable && !hasInAppRows && (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          No in-app purchases (App Store / Google Play) for this user.
        </p>
      )}

      {revenueCatHistoryHasMore && (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Only the most recent in-app payments per subscription are listed;
          older ones are in RevenueCat.
        </p>
      )}

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        isOpen={showDetailsModal}
        onClose={handleCloseModal}
        transaction={selectedTransaction}
      />
    </Card>
  );
};

export default TransactionHistoryCard;
